import React, { useEffect, useMemo, useState } from 'react';
import Icon from './AppIcon';
import MergeTagPreview from './merge-tags/MergeTagPreview';
import { personalizeForPreview } from '../utils/merge-tags';
import { getAuthHeaders } from '../lib/api';
import { formatMonthYear } from '../../lib/utils/drip-calculator';

const SAMPLE_CONTACT_FIRST_NAME = 'Alex';

function maskPhone(phone) {
  if (!phone) return 'No phone on profile';
  const digits = String(phone).replace(/\D/g, '');
  if (digits.length < 4) return phone;
  return `•••-•••-${digits.slice(-4)}`;
}

function monthOptionLabel(tp) {
  const base = tp.monthYear ? formatMonthYear(tp.monthYear) : 'Month';
  if (tp.isCurrentMonth) return `${base} (current)`;
  return base;
}

/**
 * GHL-style Preview + Send test to yourself modal.
 * Supports any monthly touchpoint in the view window plus first-touch intro.
 */
export default function PreviewTestSmsModal({
  open,
  onClose,
  touchpoints = [],
  firstTouchIntro,
  firstTouchImageUrl,
  agentPhone,
  agentName,
  gmbUrl,
}) {
  const monthOptions = useMemo(
    () =>
      [...(touchpoints || [])]
        .filter((tp) => tp?.messageText?.trim())
        .sort((a, b) => String(a.monthYear || '').localeCompare(String(b.monthYear || ''))),
    [touchpoints]
  );

  const defaultSource = useMemo(() => {
    const current = monthOptions.find((tp) => tp.isCurrentMonth);
    if (current?.id || current?.monthYear) {
      return `month:${current.id || current.monthYear}`;
    }
    if (monthOptions[0]) {
      return `month:${monthOptions[0].id || monthOptions[0].monthYear}`;
    }
    if (firstTouchIntro) return 'firstTouch';
    return 'month';
  }, [monthOptions, firstTouchIntro]);

  const [source, setSource] = useState(defaultSource);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      setSource(defaultSource);
      setResult(null);
      setError(null);
    }
  }, [open, defaultSource]);

  const selectedTouchpoint = useMemo(() => {
    if (!source?.startsWith('month:')) return null;
    const key = source.slice('month:'.length);
    return monthOptions.find((tp) => String(tp.id) === key || tp.monthYear === key) || null;
  }, [source, monthOptions]);

  const selectedMessage =
    source === 'firstTouch'
      ? firstTouchIntro || ''
      : selectedTouchpoint?.messageText || '';
  const selectedImage =
    source === 'firstTouch'
      ? firstTouchImageUrl
      : selectedTouchpoint?.imageUrl || null;

  const displayAgentName = (agentName || '').trim() || 'your agent';

  const personalized = useMemo(
    () =>
      personalizeForPreview(selectedMessage, {
        firstName: SAMPLE_CONTACT_FIRST_NAME,
        gmbUrl: gmbUrl || '',
      })
        .replace(/\{first_name\}/gi, SAMPLE_CONTACT_FIRST_NAME)
        .replace(/\{agent_name\}/gi, displayAgentName),
    [selectedMessage, gmbUrl, displayAgentName]
  );

  if (!open) return null;

  const canSend = Boolean(selectedMessage?.trim());

  const handleSend = async () => {
    if (!canSend) return;
    setSending(true);
    setError(null);
    setResult(null);
    try {
      const headers = await getAuthHeaders();
      const res = await fetch('/api/sms/test', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...headers },
        body: JSON.stringify({
          message: selectedMessage,
          mediaUrl: selectedImage || undefined,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || data.message || 'Failed to send test SMS');
      }
      setResult(data);
    } catch (err) {
      setError(err.message || 'Failed to send test SMS');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-card border border-border rounded-xl shadow-xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            <h3 className="font-semibold text-foreground text-lg">Preview &amp; Test Message</h3>
            <p className="text-sm text-muted-foreground mt-1">
              See how field merges look, then send a labeled test to yourself ({maskPhone(agentPhone)}).
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded-lg text-muted-foreground hover:bg-muted"
          >
            <Icon name="X" size={18} />
          </button>
        </div>

        <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm text-amber-950">
          <p className="font-medium">This uses sample data — not a real client.</p>
          <ul className="mt-1.5 space-y-0.5 text-amber-900/90 text-xs leading-relaxed">
            <li>
              Contact first name → <span className="font-semibold">{SAMPLE_CONTACT_FIRST_NAME}</span> (clients get their own name)
            </li>
            <li>
              Agent name → <span className="font-semibold">{displayAgentName}</span>
            </li>
          </ul>
        </div>

        <div className="mb-4">
          <label className="block text-xs font-medium text-foreground mb-1.5">
            Message to preview
          </label>
          <select
            value={source}
            onChange={(e) => {
              setSource(e.target.value);
              setResult(null);
              setError(null);
            }}
            className="w-full text-sm px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
          >
            {monthOptions.map((tp) => {
              const value = `month:${tp.id || tp.monthYear}`;
              return (
                <option key={value} value={value}>
                  {monthOptionLabel(tp)}
                </option>
              );
            })}
            {firstTouchIntro ? (
              <option value="firstTouch">First-touch intro</option>
            ) : null}
          </select>
          {monthOptions.length === 0 && !firstTouchIntro && (
            <p className="text-xs text-muted-foreground mt-1.5">No messages available yet.</p>
          )}
        </div>

        <div className="rounded-lg border border-border bg-muted/30 p-4 mb-4 space-y-3">
          <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">
            How it will look (sample contact: {SAMPLE_CONTACT_FIRST_NAME})
          </p>
          {selectedMessage ? (
            <>
              <MergeTagPreview text={selectedMessage} includeGmb={source !== 'firstTouch'} showHints={false} />
              <p className="text-sm text-foreground whitespace-pre-wrap border-t border-border pt-3">
                {personalized}
              </p>
            </>
          ) : (
            <p className="text-sm text-muted-foreground">No message available for this source.</p>
          )}
          {selectedImage && (
            <img
              src={selectedImage}
              alt="Message attachment"
              className="w-28 h-28 rounded-lg object-cover border border-border"
            />
          )}
        </div>

        {error && (
          <div className="mb-3 text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-lg p-3 flex items-start gap-2">
            <Icon name="AlertCircle" size={16} className="mt-0.5 flex-shrink-0" />
            {error}
          </div>
        )}

        {result?.success && (
          <div className="mb-3 text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg p-3 flex items-start gap-2">
            <Icon name="CheckCircle" size={16} className="mt-0.5 flex-shrink-0" />
            <span>
              Test sent to {result.toMasked || maskPhone(agentPhone)}.
              {result.mediaUrl ? ' (with image)' : ''} The SMS starts with a [TEST] note so you know “{SAMPLE_CONTACT_FIRST_NAME}” is sample data.
            </span>
          </div>
        )}

        <div className="flex gap-3">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 px-4 py-2.5 rounded-lg border border-border text-sm font-medium text-muted-foreground hover:bg-muted"
          >
            Close
          </button>
          <button
            type="button"
            onClick={handleSend}
            disabled={!canSend || sending || !agentPhone}
            title={!agentPhone ? 'Add your personal phone in Settings → Profile' : ''}
            className="flex-1 px-4 py-2.5 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 disabled:opacity-40 flex items-center justify-center gap-2"
          >
            {sending ? <Icon name="Loader2" size={16} className="animate-spin" /> : <Icon name="Send" size={16} />}
            {sending ? 'Sending…' : 'Send sample test to yourself'}
          </button>
        </div>
      </div>
    </div>
  );
}
